import { useCallback, useEffect, useRef, useState } from 'react';
import type { CategoryDTO, MenuItemDTO } from '@scan2eat/shared';
import { ApiError, apiFetch } from '../api/client.js';
import { useAuth } from '../auth/context.js';
import { formatMoney, MENU_ITEM_IMAGE_MIME_TYPES, minorToMajor, parseMoney } from '@scan2eat/shared';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { DropdownItem, DropdownMenu } from '../components/DropdownMenu.js';
import { EmptyState, ErrorState } from '../components/EmptyState.js';
import { Icon } from '../components/Icon.js';
import { Modal } from '../components/Modal.js';
import { labels } from '../labels.js';
import { prepareImageForUpload } from '../lib/image.js';
import styles from './AdminMenuPage.module.css';

interface MenuData {
  categories: CategoryDTO[];
}

type Pending =
  | { kind: 'category'; category: CategoryDTO }
  | { kind: 'item'; item: MenuItemDTO };

const PRICE_RE = /^\d+([.,]\d{1,2})?$/;

export function AdminMenuPage() {
  const { auth } = useAuth();
  const canWrite = auth?.user.role === 'MANAGER' || auth?.user.role === 'OWNER';
  // La devise decide de l'exposant : 2500 vaut 25,00 EUR mais 2500 F CFA.
  const currency = auth?.restaurant?.currency ?? 'XOF';

  const [menu, setMenu] = useState<MenuData | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);

  const [catForm, setCatForm] = useState<{ id?: string; name: string } | null>(null);
  const [itemForm, setItemForm] = useState<{ category: CategoryDTO; item?: MenuItemDTO } | null>(null);
  const [deleting, setDeleting] = useState<Pending | null>(null);

  const load = useCallback(() => {
    return apiFetch<MenuData>('/admin/menu')
      .then((d) => { setMenu(d); setLoadError(false); })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const notify = (message: string) => {
    setBanner(message);
    window.setTimeout(() => setBanner(null), 5000);
  };

  const handleError = (e: unknown, fallback: string) => {
    if (e instanceof ApiError && e.code === 'CATEGORY_NOT_EMPTY') notify(labels.menu.categoryNotEmpty);
    else if (e instanceof ApiError && e.message) notify(e.message);
    else notify(fallback);
  };

  const saveCategory = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!catForm || !catForm.name.trim()) return;
    try {
      if (catForm.id) {
        await apiFetch(`/admin/categories/${catForm.id}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: catForm.name.trim() }),
        });
      } else {
        await apiFetch('/admin/categories', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: catForm.name.trim(), isActive: true }),
        });
      }
      setCatForm(null);
      await load();
    } catch (e) {
      handleError(e, labels.errors.generic);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    const target = deleting;
    setDeleting(null);
    try {
      if (target.kind === 'category') {
        await apiFetch(`/admin/categories/${target.category.id}`, { method: 'DELETE' });
      } else {
        await apiFetch(`/admin/menu-items/${target.item.id}`, { method: 'DELETE' });
      }
      await load();
    } catch (e) {
      handleError(e, labels.errors.generic);
    }
  };

  const moveCategory = async (index: number, delta: -1 | 1) => {
    if (!menu) return;
    const cats = [...menu.categories];
    const target = index + delta;
    if (target < 0 || target >= cats.length) return;
    const a = cats[index];
    const b = cats[target];
    if (!a || !b) return;
    cats[index] = b;
    cats[target] = a;
    try {
      const d = await apiFetch<{ categories: CategoryDTO[] }>('/admin/categories/reorder', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order: cats.map((c, i) => ({ id: c.id, position: i })) }),
      });
      setMenu({ categories: d.categories });
    } catch (e) {
      handleError(e, labels.errors.generic);
    }
  };

  const toggleAvailability = async (item: MenuItemDTO) => {
    setMenu((prev) => prev ? patchItem(prev, item.id, { isAvailable: !item.isAvailable }) : prev);
    try {
      await apiFetch(`/admin/menu-items/${item.id}/availability`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isAvailable: !item.isAvailable }),
      });
    } catch {
      setMenu((prev) => prev ? patchItem(prev, item.id, { isAvailable: item.isAvailable }) : prev);
      notify(labels.errors.generic);
    }
  };

  const moveItem = async (cat: CategoryDTO, index: number, delta: -1 | 1) => {
    const items = [...cat.items];
    const target = index + delta;
    if (target < 0 || target >= items.length) return;
    const a = items[index];
    const b = items[target];
    if (!a || !b) return;
    items[index] = b;
    items[target] = a;
    try {
      await Promise.all(items.map((it, i) =>
        it.position === i ? Promise.resolve() : apiFetch(`/admin/menu-items/${it.id}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ position: i }),
        }),
      ));
      await load();
    } catch (e) {
      handleError(e, labels.errors.generic);
      await load();
    }
  };

  const allItems = menu?.categories.flatMap((c) => c.items) ?? [];
  const soldOut = allItems.filter((i) => !i.isAvailable).length;

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">{labels.menu.title}</h1>
          <p className="page-subtitle">
            {menu ? labels.menu.summary(allItems.length, soldOut) : labels.menu.subtitle}
          </p>
        </div>
        {canWrite && (
          <div className="page-actions">
            <button onClick={() => setCatForm({ name: '' })} className="btn-primary">
              <Icon name="plus" size={18} />
              {labels.menu.addCategory}
            </button>
          </div>
        )}
      </header>

      {!canWrite && (
        <p className="notice notice--info">
          <Icon name="info" size={18} />
          {labels.menu.readOnlyHint}
        </p>
      )}

      {banner && (
        <p role="alert" className="notice notice--danger">
          <Icon name="alert" size={18} />
          {banner}
        </p>
      )}

      {loading ? (
        <div className={styles.layout}>
          <div className={`skeleton ${styles.indexSkeleton}`} />
          <div className="stack">
            {Array.from({ length: 2 }).map((_, i) => <div key={i} className="skeleton" style={{ height: '16rem', borderRadius: 'var(--radius-lg)' }} />)}
          </div>
        </div>
      ) : loadError ? (
        <ErrorState onRetry={load} />
      ) : !menu || menu.categories.length === 0 ? (
        <div className="card">
          <EmptyState icon="menu" text={labels.menu.emptyMenu}>
            {canWrite && (
              <button className="btn-primary" onClick={() => setCatForm({ name: '' })}>
                <Icon name="plus" size={18} />
                {labels.menu.addCategory}
              </button>
            )}
          </EmptyState>
        </div>
      ) : (
        <div className={styles.layout}>
          <nav className={styles.index} aria-label={labels.menu.categories}>
            <p className="eyebrow">{labels.menu.categories}</p>
            <ul>
              {menu.categories.map((cat) => (
                <li key={cat.id}>
                  <a href={`#categorie-${cat.id}`}>
                    <span className="truncate">{cat.name}</span>
                    <span className="count">{cat.items.length}</span>
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div className="stack">
            {menu.categories.map((cat, ci) => (
              <section key={cat.id} id={`categorie-${cat.id}`} className="card card--flush" aria-labelledby={`titre-${cat.id}`}>
                <header className="card-header">
                  <div className="row grow">
                    <h2 id={`titre-${cat.id}`} className="card-title truncate">{cat.name}</h2>
                    <span className="count">{cat.items.length}</span>
                  </div>
                  {canWrite && (
                    <div className="row">
                      <button className="btn-ghost btn-sm" onClick={() => setItemForm({ category: cat })}>
                        <Icon name="plus" size={16} />
                        <span className={styles.hideSmall}>{labels.menu.addItem}</span>
                      </button>
                      <RowMenu
                        label={labels.menu.categoryActions(cat.name)}
                        items={[
                          { label: labels.menu.renameCategory, icon: 'edit', onClick: () => setCatForm({ id: cat.id, name: cat.name }) },
                          ...(ci > 0 ? [{ label: labels.menu.moveUp, icon: 'arrow-up' as const, onClick: () => void moveCategory(ci, -1) }] : []),
                          ...(ci < menu.categories.length - 1 ? [{ label: labels.menu.moveDown, icon: 'arrow-down' as const, onClick: () => void moveCategory(ci, 1) }] : []),
                          { label: labels.menu.deleteCategory, icon: 'trash', onClick: () => setDeleting({ kind: 'category', category: cat }), danger: true },
                        ]}
                      />
                    </div>
                  )}
                </header>

                {cat.items.length === 0 ? (
                  <p className={styles.emptyCategory}>{labels.menu.emptyCategory}</p>
                ) : (
                  <ul className="list">
                    {cat.items.map((item, ii) => (
                      <li key={item.id} className={`${styles.item} ${item.isAvailable ? '' : styles.itemOff}`}>
                        <DishThumb src={item.imageUrl} alt={item.name} />
                        <div className={styles.itemText}>
                          {canWrite ? (
                            <button type="button" className={styles.itemName} onClick={() => setItemForm({ category: cat, item })}>
                              {item.name}
                            </button>
                          ) : (
                            <span className={styles.itemName}>{item.name}</span>
                          )}
                          {item.description && <span className={styles.itemDescription}>{item.description}</span>}
                        </div>
                        <span className={`price ${styles.itemPrice}`}>{formatMoney(item.priceMinor, currency)}</span>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={item.isAvailable}
                          aria-label={labels.menu.availabilityFor(item.name)}
                          className={`switch ${styles.switch}`}
                          onClick={() => void toggleAvailability(item)}
                        >
                          <span className="switch__track" />
                          <span className={styles.switchLabel}>{item.isAvailable ? labels.menu.available : labels.menu.unavailable}</span>
                        </button>
                        {canWrite && (
                          <RowMenu
                            label={labels.menu.itemActions(item.name)}
                            items={[
                              { label: labels.menu.editItem, icon: 'edit', onClick: () => setItemForm({ category: cat, item }) },
                              ...(ii > 0 ? [{ label: labels.menu.moveItemUp, icon: 'arrow-up' as const, onClick: () => void moveItem(cat, ii, -1) }] : []),
                              ...(ii < cat.items.length - 1 ? [{ label: labels.menu.moveItemDown, icon: 'arrow-down' as const, onClick: () => void moveItem(cat, ii, 1) }] : []),
                              { label: labels.menu.deleteItem, icon: 'trash', onClick: () => setDeleting({ kind: 'item', item }), danger: true },
                            ]}
                          />
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </div>
        </div>
      )}

      {catForm && (
        <Modal
          title={catForm.id ? labels.menu.renameCategory : labels.menu.addCategory}
          onClose={() => setCatForm(null)}
          onSubmit={(e) => void saveCategory(e)}
          footer={
            <>
              <button type="button" onClick={() => setCatForm(null)} className="btn-ghost">{labels.menu.cancel}</button>
              <button type="submit" disabled={!catForm.name.trim()} className="btn-primary">{labels.menu.save}</button>
            </>
          }
        >
          <div className="field">
            <label htmlFor="category-name" className="field-label">{labels.menu.categoryName}</label>
            <input
              id="category-name"
              className="input"
              value={catForm.name}
              onChange={(e) => setCatForm({ ...catForm, name: e.target.value })}
              placeholder={labels.menu.categoryPlaceholder}
              required
              autoFocus
            />
          </div>
        </Modal>
      )}

      {itemForm && (
        <ItemDialog
          categoryId={itemForm.category.id}
          categoryName={itemForm.category.name}
          item={itemForm.item}
          currency={currency}
          onClose={() => setItemForm(null)}
          onSaved={load}
          onError={handleError}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title={deleting.kind === 'category'
            ? labels.menu.deleteCategoryConfirm(deleting.category.name)
            : labels.menu.deleteItemConfirm(deleting.item.name)}
          message={deleting.kind === 'category' ? labels.menu.deleteCategoryHelp : labels.menu.deleteItemHelp}
          confirmLabel={labels.menu.deleteItem}
          destructive
          onConfirm={() => void confirmDelete()}
          onCancel={() => setDeleting(null)}
        />
      )}
    </div>
  );
}

/** 44 px thumbnail; a missing or broken photo falls back to a line icon. */
function DishThumb({ src, alt }: { src: string | null; alt: string }) {
  const [broken, setBroken] = useState(false);
  if (src && !broken) {
    return <img src={src} alt={alt} loading="lazy" className="dish-thumb" onError={() => setBroken(true)} />;
  }
  return (
    <span className={`dish-thumb ${styles.thumbPlaceholder}`} aria-hidden="true">
      <Icon name="utensils" size={18} />
    </span>
  );
}

function patchItem(menu: MenuData, itemId: string, changes: Partial<MenuItemDTO>): MenuData {
  return {
    categories: menu.categories.map((c) => ({
      ...c,
      items: c.items.map((i) => (i.id === itemId ? { ...i, ...changes } : i)),
    })),
  };
}

interface RowMenuItem {
  label: string;
  icon: 'edit' | 'arrow-up' | 'arrow-down' | 'trash';
  onClick: () => void;
  danger?: boolean;
}

function RowMenu({ label, items }: { label: string; items: RowMenuItem[] }) {
  const anchor = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        ref={anchor}
        type="button"
        className="btn-icon btn-icon--quiet btn-icon--sm"
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name="more" size={18} />
      </button>
      <DropdownMenu open={open} onClose={() => setOpen(false)} anchorRef={anchor}>
        {items.map((item, i) => (
          <div key={item.label}>
            {item.danger && i > 0 && <div className="dropdown-separator" />}
            <DropdownItem
              onClick={() => { setOpen(false); item.onClick(); }}
              icon={<Icon name={item.icon} size={18} />}
              danger={item.danger}
            >
              {item.label}
            </DropdownItem>
          </div>
        ))}
      </DropdownMenu>
    </>
  );
}

function ItemDialog({ categoryId, categoryName, item, onClose, onSaved, onError, currency }: {
  categoryId: string;
  categoryName: string;
  item?: MenuItemDTO;
  onClose: () => void;
  onSaved: () => Promise<void>;
  onError: (e: unknown, fallback: string) => void;
  /** Decide de l'exposant des unites mineures : 0 pour le franc CFA. */
  currency: string;
}) {
  const [name, setName] = useState(item?.name ?? '');
  const [description, setDescription] = useState(item?.description ?? '');
  const [price, setPrice] = useState(item ? String(minorToMajor(item.priceMinor, currency)) : '');
  const [imageUrl, setImageUrl] = useState(item?.imageUrl ?? '');
  const [imageBroken, setImageBroken] = useState(false);
  const [saving, setSaving] = useState(false);

  // Photo choisie sur l'appareil. Elle part apres l'enregistrement du plat :
  // la route de televersement a besoin d'un identifiant, qu'une creation n'a
  // pas encore. Voir docs/adr/0023.
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);
  // Identifiant du plat une fois cree : sans lui, reessayer apres un echec de
  // televersement creerait un second plat.
  const [savedId, setSavedId] = useState(item?.id ?? null);

  const priceValid = PRICE_RE.test(price.trim());

  // L'URL d'objet occupe la memoire du navigateur tant qu'elle n'est pas
  // revoquee, et un gerant ouvre ce formulaire des dizaines de fois par jour.
  useEffect(() => () => { if (photoPreview) URL.revokeObjectURL(photoPreview); }, [photoPreview]);

  const choosePhoto = (file: File | undefined) => {
    if (!file) return;
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
    setPhotoError(null);
    setImageBroken(false);
  };

  const clearPhoto = () => {
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhoto(null);
    setPhotoPreview(null);
    setImageUrl('');
    setPhotoError(null);
  };

  const shownImage = photoPreview ?? (imageUrl && !imageBroken ? imageUrl : null);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !priceValid || saving) return;
    setSaving(true);
    // parseMoney connait l'exposant de la devise. Multiplier par cent en dur
    // rendait chaque prix saisi en franc CFA cent fois trop cher.
    const priceMinor = parseMoney(price, currency);
    if (priceMinor === null) {
      setSaving(false);
      return;
    }
    // Une photo choisie sur l'appareil decide seule du champ : envoyer une
    // adresse vide ici effacerait le pointeur vers l'ancienne image, que le
    // televersement ne pourrait plus supprimer.
    const bodyImageUrl = photo ? (item?.imageUrl ?? null) : (imageUrl.trim() || null);
    const clearingStoredPhoto = !photo && Boolean(item?.imageUrl) && imageUrl.trim() === '';

    try {
      // La suppression passe avant l'ecriture : elle a besoin de l'adresse
      // encore en base pour retirer le fichier du stockage.
      if (clearingStoredPhoto && savedId) {
        await apiFetch(`/admin/menu-items/${savedId}/image`, { method: 'DELETE' });
      }

      let itemId = savedId;
      if (itemId) {
        await apiFetch(`/admin/menu-items/${itemId}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: name.trim(),
            description: description || null,
            priceMinor,
            imageUrl: bodyImageUrl,
            categoryId,
          }),
        });
      } else {
        const created = await apiFetch<MenuItemDTO>('/admin/menu-items', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            categoryId,
            name: name.trim(),
            description: description || null,
            priceMinor,
            imageUrl: bodyImageUrl,
          }),
        });
        itemId = created.id;
        setSavedId(created.id);
      }

      if (photo && itemId) {
        setUploading(true);
        try {
          const blob = await prepareImageForUpload(photo);
          await apiFetch<MenuItemDTO>(`/admin/menu-items/${itemId}/image`, {
            method: 'POST',
            headers: { 'Content-Type': blob.type || photo.type },
            body: blob,
          });
        } catch (uploadError) {
          // Le plat est enregistre : refermer ferait croire a un echec total,
          // et rouvrir creerait un doublon. Le formulaire reste ouvert avec le
          // motif exact du refus -- format, poids ou stockage injoignable.
          setPhotoError(
            uploadError instanceof ApiError ? uploadError.message : labels.errors.generic,
          );
          await onSaved();
          return;
        } finally {
          setUploading(false);
        }
      }

      onClose();
      await onSaved();
    } catch (e) {
      onError(e, labels.errors.generic);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={item ? labels.menu.editItem : labels.menu.addItem}
      description={categoryName}
      size="wide"
      onClose={onClose}
      onSubmit={(e) => void save(e)}
      locked={saving}
      footer={
        <>
          <button type="button" onClick={onClose} className="btn-ghost" disabled={saving}>{labels.menu.cancel}</button>
          <button type="submit" disabled={!name.trim() || !priceValid || saving} className="btn-primary">{labels.menu.save}</button>
        </>
      }
    >
      <div className={styles.itemForm}>
        <div>
          <div className={styles.preview}>
            {shownImage ? (
              <img src={shownImage} alt="" onError={() => setImageBroken(true)} onLoad={() => setImageBroken(false)} />
            ) : (
              <span className={styles.previewEmpty}>
                <Icon name="image" size={28} />
                {labels.menu.noPhoto}
              </span>
            )}
          </div>

          <div className={styles.photoActions}>
            {/* Un label plutot qu'un bouton qui simule un clic : le champ de
                fichier reste atteignable au clavier et annonce par le lecteur
                d'ecran. */}
            <label htmlFor="item-photo" className="btn-secondary btn-sm">
              <Icon name="image" size={16} />
              {shownImage ? labels.menu.replacePhoto : labels.menu.choosePhoto}
            </label>
            <input
              id="item-photo"
              className="visually-hidden"
              type="file"
              accept={MENU_ITEM_IMAGE_MIME_TYPES.join(',')}
              disabled={saving}
              onChange={(e) => { choosePhoto(e.target.files?.[0]); e.target.value = ''; }}
            />
            {shownImage && (
              <button type="button" className="btn-ghost btn-sm" onClick={clearPhoto} disabled={saving}>
                {labels.menu.removePhoto}
              </button>
            )}
          </div>

          <p className="field-hint">{labels.menu.photoHint}</p>
          {photo && !uploading && <p className="field-hint">{labels.menu.photoPending}</p>}
          {uploading && <p className="field-hint" aria-live="polite">{labels.menu.photoUploading}</p>}
          {photoError && (
            <p role="alert" className="notice notice--danger" style={{ marginTop: 'var(--space-2)' }}>
              <Icon name="alert" size={18} />
              <span className="grow">{labels.menu.photoFailed} {photoError}</span>
            </p>
          )}
        </div>

        <div className="form-grid">
          <div className="field">
            <label htmlFor="item-name" className="field-label">{labels.menu.itemName}</label>
            <input id="item-name" className="input" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
          </div>

          <div className="field">
            <label htmlFor="item-desc" className="field-label">{labels.menu.itemDescription}</label>
            <textarea id="item-desc" className="input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder={labels.menu.descriptionPlaceholder} />
          </div>

          <div className="field">
            <label htmlFor="item-price" className="field-label">{labels.menu.itemPrice}</label>
            <div className={styles.priceField}>
              <input
                id="item-price"
                className="input"
                inputMode="decimal"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                required
                aria-invalid={Boolean(price) && !priceValid}
                placeholder={currency === 'XOF' ? '2500' : '12,50'}
              />
              <span className={styles.currency}>{currency === 'XOF' ? 'F CFA' : currency}</span>
            </div>
            {price && !priceValid && <p role="alert" className="field-error">{labels.menu.itemInvalidPrice}</p>}
          </div>

          <div className="field">
            <label htmlFor="item-image" className="field-label">{labels.menu.itemImage}</label>
            <input
              id="item-image"
              className="input"
              type="url"
              value={imageUrl ?? ''}
              // Une photo choisie sur l'appareil prend la main : laisser les
              // deux modifiables ferait deux reponses concurrentes a la meme
              // question.
              disabled={Boolean(photo)}
              onChange={(e) => { setImageUrl(e.target.value); setImageBroken(false); }}
              placeholder="https://..."
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}
